import { FC, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import type { ScoringType, ProductIntensityBand } from "@/lib/scoring/types";
import { getHumanMinMax } from "./MinMaxEditor";
import { ProductScoringSection } from "./ProductScoringSection";

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: ProductIntensityBand = {
  productIds: [],
  score: 0,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as ProductIntensityBand[];
  if (typed.length === 0) return true;
  const productIds = typed[0]?.productIds ?? [];
  if (productIds.length === 0) return true;
  return typed.some((band, i) => {
    const others = typed.filter((_, j) => j !== i);
    return getBandError(band, others) !== null;
  });
}

export function getBandSummary(band: unknown): string {
  const b = band as ProductIntensityBand;
  return getHumanMinMax(b.min, b.max);
}

// ── Validation ────────────────────────────────────────────────────────────

function getBandError(
  band: ProductIntensityBand,
  others: ProductIntensityBand[]
): string | null {
  if (!band.score) return "Points can't be empty";

  if (band.min === undefined && band.max === undefined) return "Range is empty";
  if (band.min !== undefined && band.max !== undefined && band.min > band.max)
    return "Min must be ≤ max";

  const rangeMin = band.min ?? -Infinity;
  const rangeMax = band.max ?? Infinity;
  if (
    others.some((o) => {
      const oMin = o.min ?? -Infinity;
      const oMax = o.max ?? Infinity;
      return rangeMin < oMax && oMin < rangeMax;
    })
  )
    return "Intervals intersect";

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const ProductIntensityEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.productIntensity ?? [];
  const [productIds, setProductIds] = useState<number[]>(
    bands[0]?.productIds ?? []
  );

  const handleProductsChange = useCallback(
    (ids: number[]) => {
      setProductIds(ids);
      onChange({
        ...criterion,
        productIntensity: bands.map((b) => ({ ...b, productIds: ids })),
      });
    },
    [bands, criterion, onChange]
  );

  const updateBand = (index: number, patch: Partial<ProductIntensityBand>) => {
    const newBands = bands.map((b, i) =>
      i === index ? { ...b, ...patch, productIds } : { ...b, productIds }
    );
    onChange({ ...criterion, productIntensity: newBands });
  };

  const removeBand = (index: number) => {
    onChange({
      ...criterion,
      productIntensity: bands
        .filter((_, i) => i !== index)
        .map((b) => ({ ...b, productIds })),
    });
  };

  const addBand = () => {
    onChange({
      ...criterion,
      productIntensity: [
        ...bands.map((b) => ({ ...b, productIds })),
        { productIds, score: 0 },
      ],
    });
  };

  return (
    <Box>
      <ProductScoringSection
        productIds={productIds}
        onProductsChange={handleProductsChange}
      />

      {/* Bands */}
      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty =
          band.min !== undefined || band.max !== undefined || band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        return (
          <Box key={i} sx={{ mb: error ? 0.5 : 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr 72px auto",
                gap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Min Intensity"
                value={band.min ?? ""}
                error={!!error && error !== "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    min:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  })
                }
                inputProps={{ step: 1, min: 0 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center" }}
              >
                —
              </Typography>
              <TextField
                size="small"
                type="number"
                label="Max Intensity"
                value={band.max ?? ""}
                error={!!error && error !== "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    max:
                      e.target.value === ""
                        ? undefined
                        : Number(e.target.value),
                  })
                }
                inputProps={{ step: 1, min: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="Pts"
                value={band.score === 0 ? "" : band.score}
                error={error === "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{ min: 0, step: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.25, mb: 0.75 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600, mt: 0.5 }}
      >
        ADD BAND
      </Button>
    </Box>
  );
};
